import { IconCircleCheck } from "@tabler/icons-react";
import { Heading, Text } from "../../../../components/common/typography";
import type { OrderStatus } from "../../../../components/common/chip";

type StepState = "done" | "current" | "pending";

interface TimelineStep {
  label: string;
  date: string | null;
  state: StepState;
}

const STEP_DEFINITIONS = [
  { label: "Order Placed" },
  { label: "Shipped" },
  { label: "Delivered" },
  { label: "Validated" },
];

const CURRENT_INDEX: Partial<Record<OrderStatus, number>> = {
  processing: 0,
  inTransit: 1,
  delivered: 2,
  validated: 3,
};

function formatTimelineDate(date: string | null | undefined): string | null {
  if (!date) return null;

  const parsed = new Date(date.replace(" ", "T"));
  if (Number.isNaN(parsed.getTime())) return null;

  return new Intl.DateTimeFormat(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(parsed);
}

function buildSteps(
  status: OrderStatus,
  dates: {
    orderedAt?: string | null;
    shippedAt?: string | null;
    deliveredAt?: string | null;
    validatedAt?: string | null;
  }
): TimelineStep[] {
  const current = CURRENT_INDEX[status] ?? 0;
  const orderedAt = formatTimelineDate(dates.orderedAt);
  const shippedAt = formatTimelineDate(dates.shippedAt);
  const deliveredAt = formatTimelineDate(dates.deliveredAt);
  const validatedAt = formatTimelineDate(dates.validatedAt);

  const steps = [
    { label: "Order Placed", date: orderedAt },
    { label: "Shipped", date: shippedAt },
    { label: "Delivered", date: deliveredAt },
    { label: "Validated", date: validatedAt },
  ];

  return STEP_DEFINITIONS.map((step, i) => ({
    label: step.label,
    state: (i < current ? "done" : i === current ? "current" : "pending") as StepState,
    date: i > current ? null : steps[i].date,
  }));
}

function StepIcon({ state }: { state: StepState }) {
  if (state === "current") {
    return (
      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-accentGreen">
        <IconCircleCheck size={20} className="text-bgWhite" />
      </div>
    );
  }
  if (state === "done") {
    return <IconCircleCheck size={22} className="text-accentGreen" />;
  }
  return <div className="h-[22px] w-[22px] rounded-full border-2 border-textMuted bg-bgWhite" />;
}

export function SellerOrderTimeline({
  status,
  orderedAt,
  shippedAt,
  deliveredAt,
  validatedAt,
}: {
  status: OrderStatus;
  orderedAt?: string | null;
  shippedAt?: string | null;
  deliveredAt?: string | null;
  validatedAt?: string | null;
}) {
  const steps = buildSteps(status, { orderedAt, shippedAt, deliveredAt, validatedAt });
  return (
    <div className="flex flex-col gap-4 rounded-2xl border border-borderDefault bg-bgWhite px-6 py-5">
      <Heading variant="h3" tone="dark">Order Timeline</Heading>
      <div className="flex flex-col">
        {steps.map((step, i) => (
          <div key={step.label} className="flex gap-4">
            <div className="flex flex-col items-center">
              <div className="flex h-8 w-8 items-center justify-center">
                <StepIcon state={step.state} />
              </div>
              {i < steps.length - 1 && (
                <div className={`my-1 w-0.5 flex-1 min-h-[48px] ${step.state === "done" ? "bg-accentGreen" : "bg-borderDefault"}`} />
              )}
            </div>
            <div className="flex flex-col gap-0.5 pb-5 last:pb-0">
              <Text variant={step.state !== "pending" ? "bodyBold" : "body"} tone={step.state !== "pending" ? "dark" : "muted"}>
                {step.label}
              </Text>
              <Text variant="bodySm" tone="muted">{step.date ?? "–"}</Text>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
